import * as React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Section, StateView, formatUTC, formatDateTime, useAuth, useToast } from "@ozpanel/ui";

/**
 * Time zones (A5 / P9). Servers, databases and logs stay UTC. An ACCOUNT has a zone (its PHP default,
 * its cron jobs' and apps' TZ, how its times are shown); a LOGIN may override the zone it is SHOWN
 * times in. UTC is always on hover. Field names are the server's: /tenants/{id}/time-zone
 * {time_zone, effective, default, feeds, applied} and /auth/time-zone {display_time_zone, time_zone}.
 */

interface AccountZone {
  time_zone: string;
  effective: string;
  default: string;
  feeds: string;
  applied?: boolean;
}

/** Every IANA zone the browser knows, for the picker's suggestions. */
function zoneNames(): string[] {
  try {
    const f = (Intl as unknown as { supportedValuesOf?: (k: string) => string[] }).supportedValuesOf;
    return f ? f("timeZone") : [];
  } catch {
    return [];
  }
}

function ZoneInput({ id, value, onChange, placeholder }: { id: string; value: string; onChange: (v: string) => void; placeholder: string }) {
  const names = React.useMemo(zoneNames, []);
  return (
    <>
      <input
        className="w-64 rounded border border-line bg-surface px-2 py-1 font-mono text-[13px]"
        list={id}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
      <datalist id={id}>
        {names.map((n) => (
          <option key={n} value={n} />
        ))}
      </datalist>
    </>
  );
}

/** An account's zone: editable by the account itself and by the operator. */
export function AccountTimeZonePanel({ tenantId, bare }: { tenantId: number; bare?: boolean }) {
  const { client, refresh } = useAuth();
  const { notify } = useToast();
  const qc = useQueryClient();
  const key = ["tenant-time-zone", tenantId];
  const q = useQuery({
    queryKey: key,
    queryFn: () => client.apiFetch<AccountZone>(`/tenants/${tenantId}/time-zone`),
    retry: false,
  });
  const [draft, setDraft] = React.useState<string | null>(null);
  const value = draft ?? q.data?.time_zone ?? "";
  const save = useMutation({
    mutationFn: (z: string) =>
      client.apiFetch<AccountZone>(`/tenants/${tenantId}/time-zone`, { method: "PUT", body: JSON.stringify({ time_zone: z }) }),
    onSuccess: (r) => {
      setDraft(null);
      qc.setQueryData(key, r);
      void refresh();
      notify({
        kind: "success",
        title: `Account time zone: ${r.effective}`,
        body: r.applied ? "Applied everywhere: PHP, scheduled tasks and apps (an app whose time zone changed restarts)." : "Unchanged.",
      });
    },
    onError: (e) => notify({ kind: "error", title: "The time zone was not changed", body: e instanceof Error ? e.message : String(e) }),
  });
  const body = (
      <div className={(bare ? "" : "p-4 ") + "space-y-2 text-[13px] leading-relaxed"}>
        <StateView loading={q.isLoading} error={q.error} errorContext="the account's time zone">
          {q.data && (
            <>
              <div>
                In force: <b className="font-mono">{q.data.effective}</b>
                {q.data.time_zone ? null : <span className="text-muted"> (not chosen — the default, {q.data.default})</span>}
                {" · now "}
                <span title={formatUTC(Date.now())}>{new Date().toLocaleString(undefined, { timeZone: q.data.effective, timeZoneName: "short" })}</span>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <ZoneInput id={`tz-${tenantId}`} value={value} onChange={setDraft} placeholder="UTC (e.g. Europe/Berlin)" />
                <Button size="sm" disabled={save.isPending || draft === null} onClick={() => save.mutate(value.trim())}>
                  Save
                </Button>
                {q.data.time_zone ? (
                  <Button size="sm" variant="ghost" disabled={save.isPending} onClick={() => save.mutate("")}>
                    Back to UTC
                  </Button>
                ) : null}
              </div>
              <div className="text-muted">{q.data.feeds}</div>
            </>
          )}
        </StateView>
      </div>
  );
  return bare ? (
    body
  ) : (
    <Section title="Time zone" hint="the account's zone — servers, databases and logs stay UTC">
      {body}
    </Section>
  );
}

/** This login's display zone: only how times are SHOWN; changes nothing that runs. */
export function DisplayTimeZonePanel({ bare }: { bare?: boolean } = {}) {
  const { client, who, refresh, impersonating } = useAuth();
  const { notify } = useToast();
  const [draft, setDraft] = React.useState<string | null>(null);
  const value = draft ?? who?.display_time_zone ?? "";
  const save = useMutation({
    mutationFn: (z: string) =>
      client.apiFetch<{ display_time_zone: string; time_zone: string }>("/auth/time-zone", {
        method: "PUT",
        body: JSON.stringify({ display_time_zone: z }),
      }),
    onSuccess: (r) => {
      setDraft(null);
      void refresh();
      notify({ kind: "success", title: `Times are now shown in ${r.time_zone}` });
    },
    onError: (e) => notify({ kind: "error", title: "The display zone was not changed", body: e instanceof Error ? e.message : String(e) }),
  });
  const follows = who?.account_time_zone ?? "UTC";
  const body = (
      <div className={(bare ? "" : "p-4 ") + "space-y-2 text-[13px] leading-relaxed"}>
        <div>
          Shown in <b className="font-mono">{who?.time_zone ?? "UTC"}</b>
          {who?.display_time_zone ? null : <span className="text-muted"> (following {who?.account_time_zone ? "the account" : "UTC"})</span>}
          {" · e.g. now "}
          <span title={formatUTC(Date.now())}>{formatDateTime(Date.now())}</span>
        </div>
        {who?.time_zone_error ? <div className="text-warn">{who.time_zone_error} — times are shown in UTC.</div> : null}
        {impersonating ? (
          <div className="text-muted">Only the login itself can change its display zone.</div>
        ) : (
          <div className="flex flex-wrap items-center gap-2">
            <ZoneInput id="tz-display" value={value} onChange={setDraft} placeholder={`follow ${follows}`} />
            <Button size="sm" disabled={save.isPending || draft === null} onClick={() => save.mutate(value.trim())}>
              Save
            </Button>
            {who?.display_time_zone ? (
              <Button size="sm" variant="ghost" disabled={save.isPending} onClick={() => save.mutate("")}>
                Follow {follows}
              </Button>
            ) : null}
          </div>
        )}
      </div>
  );
  return bare ? (
    body
  ) : (
    <Section title="Display time zone" hint="how this login sees times — UTC on hover">
      {body}
    </Section>
  );
}

/**
 * TimeZoneCard — the two time-zone settings in ONE card: the account's zone (when its schedules
 * run, what its apps and PHP see) and this login's display zone (only how times are shown). They
 * were two cards with near-identical titles at the foot of Security.
 */
export function TimeZoneCard({ tenantId }: { tenantId?: number | null }) {
  return (
    <Section title="Time zone">
      <div className="space-y-5">
        {tenantId != null && (
          <div>
            <div className="mb-1.5 text-[13.5px] font-medium">For the account</div>
            <div className="mb-2 text-[12.5px] text-muted">When scheduled tasks run, and the time your apps and sites see.</div>
            <AccountTimeZonePanel tenantId={tenantId} bare />
          </div>
        )}
        <div className={tenantId != null ? "border-t border-line pt-4" : undefined}>
          <div className="mb-1.5 text-[13.5px] font-medium">How times are shown to you</div>
          <DisplayTimeZonePanel bare />
        </div>
      </div>
    </Section>
  );
}

